"use client"
import type { PublicConversationSession } from "@/modules/chatbot/lifecycle-contracts"
import { uploadInputLimit, type UploadPolicySettings } from "@/modules/uploads/policy"
import { mapWithConcurrency } from "@/lib/async-pool"

import { api, ApiError } from "@/lib/api-client"
import { publicSupportAccessEnded } from "./public-support-access"
import { publishRecordUpdate } from "@/features/workspace/record-updates"
import { presentRemoteMessage, communicationActorId as actorId } from "./message-adapter"
import { actors, isStaff, staffIds, mergeConversationMessages, type ActorId, type Attachment, type Channel, type CommunicationNotification, type CommunicationsState, type Conversation, type Message, type Presence, type SoundPreferences, type Voice } from "./model"
import type { ActorProfile, Bootstrap, BootstrapResponse, NotificationPage, RemoteMessage, RemoteThread, ThreadDetail, ThreadPage, ThreadPreferences } from "./contracts"

const initial: CommunicationsState = { cacheScope:crypto.randomUUID(), conversations: [], notifications: [], presence: { ai: "available", system: "available", unknown: "offline" }, viewer: "unknown", selectedId: "", sounds: true, soundTone: "soft", soundVolume: .6, aiPaused: false, simulationOpen: false, revision: 0, status: "idle" }
let state = initial
let notificationWake: ReturnType<typeof setTimeout> | undefined
let notificationReadGeneration = 0
const listeners = new Set<() => void>(), urls = new Map<string, Blob>()
const retryKeys = new Map<string, string>(), draftTimers = new Map<string, ReturnType<typeof setTimeout>>()
const uploadKeys = new WeakMap<Blob, string>()
const dirtyDrafts = new Set<string>(), threadRequests = new Map<string, Promise<void>>()
const preferenceWrites = new Map<string, Promise<void>>()
const watchedThreads = new Map<string, { retained: number; active: number }>()
const inlineOwners = new Map<symbol,{owner:string;openChat?:()=>void}>()
const currentInlineOwner=()=>[...inlineOwners.values()].at(-1)
type OwnChanges = Partial<SoundPreferences> & { presence?: Presence }
let ownChanges: OwnChanges = {}, ownTimer: ReturnType<typeof setTimeout> | undefined, ownWrite = Promise.resolve()
let epoch = 0, references = 0, poll: ReturnType<typeof setTimeout> | undefined, bootstrapPromise: Promise<void> | undefined
let lifecycle = new AbortController(), eventCursor = "", bootstrappedAt = 0, lastIdentityCheck = 0, listSearch = "", reading = new Set<string>(), listGeneration = 0
function surface() { return typeof location === "undefined" ? "crm" : location.hostname.startsWith("crm.") || /^\/crm(?:\/|$)/.test(location.pathname) ? "crm" : location.hostname.startsWith("my.") || /^\/my(?:\/|$)/.test(location.pathname) ? "customer" : "public" }
function endpoint(path = "") { const url = new URL("/api/communications" + path, location.origin); url.searchParams.set("surface", surface()); return url.pathname + url.search }
function emit(changes: Partial<CommunicationsState>) { state = { ...state, ...changes, revision: state.revision + 1 }; listeners.forEach(listener => listener()) }
function clearIdentity() {
  notificationReadGeneration++
  clearTimeout(notificationWake); notificationWake = undefined
  epoch++; listGeneration++; lifecycle.abort(); lifecycle = new AbortController(); eventCursor = ""; listSearch = ""; bootstrapPromise = undefined
  retryKeys.clear(); draftTimers.forEach(clearTimeout); draftTimers.clear(); dirtyDrafts.clear(); threadRequests.clear(); preferenceWrites.clear(); reading = new Set()
  watchedThreads.clear()
  clearTimeout(ownTimer); ownChanges = {}; ownWrite = Promise.resolve()
  urls.forEach((_, url) => URL.revokeObjectURL(url)); urls.clear()
  for (const key of Object.keys(actors)) if (!["ai", "chatgpt", "system", "unknown"].includes(key)) delete actors[key]
  staffIds.splice(0); state = { ...initial,cacheScope:crypto.randomUUID(),inlineOwner:currentInlineOwner()?.owner, revision: state.revision + 1 }; listeners.forEach(listener => listener())
}
async function request<T>(path: string, options?: RequestInit) {
  const revision = epoch
  const result = await api<T>(endpoint(path), { ...options, signal: options?.signal ? AbortSignal.any([lifecycle.signal, options.signal]) : lifecycle.signal }).catch(error => {
    if (error instanceof ApiError && error.code === "public_preview_required" && surface() === "public") window.dispatchEvent(new Event(publicSupportAccessEnded))
    throw error
  })
  if (revision !== epoch) throw new DOMException("The signed-in account changed.", "AbortError")
  return result
}
function report(error: unknown) {
  if (error instanceof DOMException && error.name === "AbortError") return
  if (error instanceof ApiError && error.status === 401) { clearIdentity(); emit({ status: "error", error: error.message }); return }
  emit({ error: error instanceof Error ? error.message : "The conversation could not be updated." })
}
function background(operation: Promise<unknown>) { void operation.catch(report) }
async function command<T>(path: string, data: Record<string, unknown>, method = "POST", headers?: Record<string, string>) {
  const signature = JSON.stringify([state.viewer, path, data]), idempotencyKey = retryKeys.get(signature) ?? crypto.randomUUID()
  retryKeys.set(signature, idempotencyKey)
  const result = await request<T>(path, { method, headers, body: JSON.stringify({ ...data, idempotencyKey }) })
  retryKeys.delete(signature); return result
}
function profiles(items: ActorProfile[]) {
  const presence = { ...state.presence }
  for (const person of items) {
    const id = actorId(person)
    actors[id] = { name: person.name, role: person.kind === "guest" ? "visitor" : person.kind, photo: person.kind === "ai" ? id === "ai" ? actors.ai.photo : undefined : person.photo ?? (person.kind === "system" && person.name === "Danaro" ? "/panel-assets/shell/danaro-logo.svg" : undefined) }
    presence[id] = person.presence
  }
  return presence
}
function message(row: RemoteMessage, threadId: string): Message {
  return presentRemoteMessage(row, id => endpoint("/" + threadId + "/attachments/" + id))
}
function thread(row: RemoteThread, previous?: Conversation): Conversation {
  const viewer = state.viewer, prefs = row.preferences, hasDirtyDraft = dirtyDrafts.has(row.id)
  return { ...previous, id: row.id, subject: row.subject || (row.channel === "team" ? "Team conversation" : "Support conversation"), channel: row.channel === "customer" ? "portal" : row.channel === "info_support" ? "info" : row.channel,
    mailbox: row.channel === "support" ? "support@danarolimo.com" : row.channel === "info_support" ? "info@danarolimo.com" : undefined, submissionSource: row.submissionSource ?? previous?.submissionSource, contactForm: row.contactForm ?? previous?.contactForm, invoiceContext: row.invoiceContext??null, companyContext: row.companyContext ?? null, companyAccess: row.companyAccess ?? true,
    labels: row.channel === "info_support" ? ["Support"] : [], customer: row.correspondent ? actorId(row.correspondent) : viewer, participants: row.participants.map(actorId),
    assignedTo: row.assignedUserId ? "staff:" + row.assignedUserId : null, mode: row.state, waiting: row.waitingOn, lastCustomerMessageAt:row.lastCustomerMessageAt?Date.parse(row.lastCustomerMessageAt):previous?.lastCustomerMessageAt, folder: prefs.folder, snoozedUntil: prefs.snoozedUntil ? new Date(prefs.snoozedUntil).getTime() : undefined,
    messages: previous?.loaded ? previous.messages : row.preview ? [message(row.preview, row.id)] : previous?.messages ?? [], unread: { [viewer]: row.unread },
    draft: hasDirtyDraft ? previous?.draft ?? {} : { [viewer]: prefs.draft }, internalDraft: hasDirtyDraft ? previous?.internalDraft ?? {} : { [viewer]: prefs.internalDraft ?? "" },
    typing: row.typing ?? previous?.typing ?? false, generation: row.generation ?? 0, version: row.version, preferenceVersion: prefs.version, updatedAt: new Date(row.updatedAt).getTime(), assistant: row.kind === "private_ai", assistantProvider: row.aiProvider ?? "openai" }
}
function updateThread(id: string, update: (current: Conversation) => Conversation) { emit({ conversations: state.conversations.map(current => current.id === id ? update(current) : current) }) }
async function loadList(append = false) {
  if (surface() === "public") {
    if (state.selectedId) await loadThread(state.selectedId)
    return
  }
  const generation = append ? listGeneration : ++listGeneration
  const query = new URLSearchParams({ limit: "30", ...(listSearch ? { q: listSearch } : {}), ...(append && state.nextCursor ? { cursor: state.nextCursor } : {}) })
  const page = await request<ThreadPage>("?" + query)
  if (generation !== listGeneration) return
  const presence = profiles(page.profiles), known = new Map(state.conversations.map(item => [item.id, item]))
  const items = page.items.map(item => thread(item, known.get(item.id)))
  const preserved = append ? state.conversations : state.conversations.filter(item => (item.id === state.selectedId || watchedThreads.has(item.id) || dirtyDrafts.has(item.id)) && !items.some(row => row.id === item.id))
  emit({ conversations: [...new Map([...preserved, ...items].map(item => [item.id, item])).values()], nextCursor: page.nextCursor, presence })
}
async function loadThread(id: string, older = false) {
  if (threadRequests.has(id)) return threadRequests.get(id)
  const current = state.conversations.find(item => item.id === id), requestEpoch = epoch
  const operation = (async () => {
    emit({ loadingThread: id })
    const data = await request<ThreadDetail>("/" + id + "?limit=40&" + (older && current?.previousCursor ? "before=" + encodeURIComponent(current.previousCursor) : "tail=true"))
    const presence = profiles(data.profiles), previous = state.conversations.find(item => item.id === id), loaded = thread(data.thread, previous)
    loaded.aiFailure = data.thread.aiFailure
    loaded.aiRun = data.thread.aiRun
    const incoming = data.messages.map(item => message(item, id))
    loaded.messages = mergeConversationMessages(previous?.loaded ? previous.messages : [], incoming, older); loaded.loaded = true
    loaded.cursor = older ? previous?.cursor ?? data.readCursor ?? data.cursor : data.readCursor ?? data.cursor
    loaded.previousCursor = older || !previous?.loaded ? data.previousCursor : previous.previousCursor
    emit({ conversations: previous ? state.conversations.map(item => item.id === id ? loaded : item) : [loaded, ...state.conversations], presence, loadingThread: undefined })
  })().catch(error => {
    if (requestEpoch === epoch && error instanceof ApiError && [403, 404].includes(error.status)) {
      clearTimeout(draftTimers.get(id)); draftTimers.delete(id); dirtyDrafts.delete(id)
      emit({ conversations: state.conversations.filter(item => item.id !== id), selectedId: state.selectedId === id ? "" : state.selectedId })
    }
    throw error
  }).finally(() => { if (requestEpoch === epoch) { threadRequests.delete(id); if (state.loadingThread === id) emit({ loadingThread: undefined }) } })
  threadRequests.set(id, operation); return operation
}
async function loadNotifications() {
  if (surface() === "public") { emit({ notifications: [] }); return }
  const generation = ++notificationReadGeneration
  try {
  const data = await request<NotificationPage>("/notifications?folder=inbox")
  if (generation !== notificationReadGeneration) return
  const presence = profiles(data.profiles)
  emit({ presence, notificationActorRole: data.actorRole, notificationNextWakeAt: data.nextWakeAt ? Date.parse(data.nextWakeAt) : null, notificationUnreadCount: data.unreadCount, notificationActiveCount: data.activeCount, notificationLifecycleAvailable: data.lifecycleAvailable === true, notificationError: undefined, notifications: notificationItems(data) })
  scheduleNotificationWake()
  } catch (error) {
    if (generation !== notificationReadGeneration) return
    if (!(error instanceof DOMException && error.name === "AbortError")) emit({ notificationError: error instanceof Error ? error.message : "Notifications could not refresh.", ...(error instanceof ApiError && [401,403,404,410].includes(error.status) ? { notifications: [], notificationActorRole: undefined, notificationNextWakeAt: null, notificationUnreadCount: undefined, notificationActiveCount: undefined, notificationLifecycleAvailable: false } : {}) })
    if (error instanceof ApiError && [401,403,404,410].includes(error.status)) scheduleNotificationWake()
    throw error
  }
}
function notificationItems(data: NotificationPage): CommunicationNotification[] {
  return data.items.map(item => ({ id: item.id, conversationId: item.threadId, messageId: item.messageId, target: state.viewer, author: actorId(item.author),
    title: item.title, description: item.description, at: new Date(item.at).getTime(), category: item.category ?? (item.kind === "assignment" ? "operations" : "messages"), read: item.read, dismissed: item.dismissed, archived: item.archived ?? item.dismissed, deleted: item.deleted, snoozedUntil: item.snoozedUntil ? Date.parse(item.snoozedUntil) : null,
    banner: !item.bannerDismissed && new Date(item.at).getTime() > bootstrappedAt, count: 1, kind: item.kind, presentation: item.presentation }))
}
function scheduleNotificationWake() {
  clearTimeout(notificationWake); notificationWake = undefined
  if (!listeners.size) return
  const next = Math.min(...state.notifications.filter(item => !item.archived && !item.deleted && (item.snoozedUntil ?? 0) > Date.now()).map(item => item.snoozedUntil!), (state.notificationNextWakeAt ?? 0) > Date.now() ? state.notificationNextWakeAt! : Infinity)
  if (!Number.isFinite(next)) return
  const revision = epoch
  notificationWake = setTimeout(() => {
    if (revision !== epoch || !listeners.size) return
    const now = Date.now()
    emit({ notificationWakeRevision: (state.notificationWakeRevision ?? 0) + 1, notifications: state.notifications.map(item => ({ ...item, dismissed: Boolean(item.archived || item.deleted || (item.snoozedUntil ?? 0) > now) })) })
    background(loadNotifications())
    scheduleNotificationWake()
  }, Math.min(Math.max(0,next-Date.now())+20,2147483647))
}
async function changeNotification(id: string, patch: { dismissed?: boolean; deleted?: boolean; snoozedUntil?: string | null; read?: boolean }) {
  const revision = epoch
  await command("/notifications", { eventId: id, ...patch, bannerDismissed: true }, "PATCH")
  if (revision !== epoch) return
  notificationReadGeneration++
  emit({ notificationError: undefined, notifications: state.notifications.map(item => {
    if (item.id !== id) return item
    const archived = patch.dismissed ?? item.archived ?? item.dismissed, deleted = patch.deleted ?? item.deleted, snoozedUntil = patch.snoozedUntil !== undefined ? patch.snoozedUntil ? Date.parse(patch.snoozedUntil) : null : item.snoozedUntil
    return { ...item, archived, deleted, snoozedUntil, read: patch.read ?? item.read, banner: false, dismissed: Boolean(archived || deleted || (snoozedUntil ?? 0) > Date.now()) }
  }) })
  scheduleNotificationWake()
  background(loadNotifications())
}
async function reconcileConversationAccess() {
  const known = state.conversations.map(item => item.id), permitted = new Set<string>()
  for (let offset = 0; offset < known.length; offset += 100) {
    const result = await request<{ ids: string[] }>("/access-check", { method: "POST", body: JSON.stringify({ ids: known.slice(offset, offset + 100) }) })
    result.ids.forEach(id => permitted.add(id))
  }
  const denied = new Set(known.filter(id => !permitted.has(id)))
  if (!denied.size) return
  for (const id of denied) { clearTimeout(draftTimers.get(id)); draftTimers.delete(id); dirtyDrafts.delete(id) }
  emit({ conversations: state.conversations.filter(item => !denied.has(item.id)), selectedId: denied.has(state.selectedId) ? "" : state.selectedId,
    notifications: state.notifications.filter(item => !item.conversationId || !denied.has(item.conversationId)),
    ...(denied.has(state.selectedId) || [...watchedThreads].some(([id, watch]) => denied.has(id) && watch.active > 0) ? { error: "Your conversation access changed. Return to the current record or choose another conversation." } : {}) })
}
async function bootstrap() {
  if (bootstrapPromise) return bootstrapPromise
  bootstrapPromise = (async () => {
    if (state.status !== "ready") emit({ status: "loading", error: undefined })
    try {
      const data = await request<BootstrapResponse>("/bootstrap")
      if ("guestSessionRequired" in data) {
        // An unchanged anonymous identity must not abort the first send while
        // its security proof is pending. Expired known identities still reset.
        if (state.viewer !== "unknown") clearIdentity()
        lastIdentityCheck = Date.now(); emit({ status: "idle" }); return
      }
      const id = actorId(data.actor), changed = id !== state.viewer || JSON.stringify(data.permissions) !== JSON.stringify(state.permissions)
      if (changed && state.viewer !== "unknown") clearIdentity()
      if (changed) { bootstrappedAt = Date.now(); eventCursor = data.eventCursor }
      staffIds.splice(0, staffIds.length, ...data.team.profiles.map(actorId))
      const presence = profiles([data.actor, ...data.team.profiles])
      emit({ viewer: id, presence, permissions: data.permissions, teamMore: data.team.more,
        sounds: data.preferences.sounds, soundTone: data.preferences.soundTone, soundVolume: data.preferences.soundVolume,
        acceptingChats: data.preferences.acceptingChats, maxActiveChats: data.preferences.maxActiveChats, preferencesVersion: data.preferences.version, status: "ready", error: undefined })
      lastIdentityCheck = Date.now()
      if (changed || !state.conversations.length) await Promise.all([loadList(), loadNotifications()])
      else {
        await reconcileConversationAccess()
        // Membership can be revoked without a readable event. Recheck mounted retained threads.
        const retained = new Set([...watchedThreads.keys(), ...(state.selectedId ? [state.selectedId] : [])])
        const results = await Promise.allSettled([...retained].map(id => loadThread(id)))
        for (const result of results) if (result.status === "rejected") report(result.reason)
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return
      if (error instanceof ApiError && error.code === "guest_session_required" && surface() === "public") { if (state.viewer !== "unknown") clearIdentity(); lastIdentityCheck = Date.now(); emit({ status: "idle" }); return }
      if (error instanceof ApiError && error.status === 403) clearIdentity()
      report(error); if (state.status === "loading") emit({ status: "error" })
    }
  })().finally(() => { bootstrapPromise = undefined })
  return bootstrapPromise
}
async function tick() {
  if (!references) return
  try {
    if (document.visibilityState === "visible") {
      if (Date.now() - lastIdentityCheck > 45000) await bootstrap()
      if (state.status === "ready" && eventCursor) {
        const data = await request<{ events: { threadId: string | null; action?: string }[]; nextCursor: string }>("/events?after=" + encodeURIComponent(eventCursor))
        if (data.events.length) {
          if (data.events.some(event => ["ai-action-confirmed", "contact-captured", "intake-confirmed", "followup-created", "price-discussed"].includes(event.action ?? ""))) publishRecordUpdate(["tasks", "journeys"])
          const redacted = new Set(data.events.flatMap(event => event.action === "privacy-redacted" && event.threadId ? [event.threadId] : []))
          if (redacted.size) {
            for (const id of redacted) { clearTimeout(draftTimers.get(id)); draftTimers.delete(id); dirtyDrafts.delete(id) }
            emit({ conversations: state.conversations.map(item => redacted.has(item.id) ? { ...item, messages: [], loaded: false, draft: {}, internalDraft: {}, cursor: undefined, previousCursor: null } : item) })
          }
          const visible = new Set([...watchedThreads].filter(([, count]) => count.active > 0).map(([id]) => id).concat(state.selectedId ? [state.selectedId] : []))
          const changed = [...visible].filter(id => data.events.some(event => event.threadId === id))
          await Promise.all([data.events.some(event => event.threadId) && loadList(), loadNotifications(), ...changed.map(id => loadThread(id))])
          emit({ notificationWakeRevision: (state.notificationWakeRevision ?? 0) + 1 })
        }
        eventCursor = data.nextCursor
      }
    }
  } catch (error) { report(error) }
  finally { if (references) poll = setTimeout(() => void tick(), document.visibilityState === "visible" ? 5000 : 20000) }
}
const focus = () => { background(bootstrap()) }
export function connectCommunications() {
  references++
  if (references === 1) { window.addEventListener("focus", focus); background(bootstrap()); poll = setTimeout(() => void tick(), 5000) }
  return () => { references--; if (!references) { clearTimeout(poll); window.removeEventListener("focus", focus); clearIdentity() } }
}
async function mutate(id: string, action: string, extra: Record<string, unknown> = {}) {
  const current = state.conversations.find(item => item.id === id)
  await command("/" + id, { action, ...(isStaff(state.viewer) && action !== "read" ? { expectedVersion: current?.version } : {}), ...extra })
  await Promise.all([loadThread(id), loadNotifications()]); emit({ error: undefined })
}
function preference(id: string, changes: Record<string, unknown>): Promise<void> {
  const requestEpoch = epoch
  const previous = preferenceWrites.get(id) ?? Promise.resolve()
  const next = previous.catch(() => {}).then(async () => {
    if (requestEpoch !== epoch) throw new DOMException("The signed-in account changed.", "AbortError")
    const current = state.conversations.find(item => item.id === id)
    if (!current) return
    const result = await command<ThreadPreferences>("/" + id + "/preferences", { expectedVersion: current.preferenceVersion ?? 0, ...changes }, "PATCH")
    updateThread(id, item => ({ ...item, folder: result.folder, snoozedUntil: result.snoozedUntil ? new Date(result.snoozedUntil).getTime() : undefined, preferenceVersion: result.version }))
  })
  preferenceWrites.set(id, next); return next
}
async function persistDraft(id: string) {
  clearTimeout(draftTimers.get(id)); draftTimers.delete(id)
  const current = state.conversations.find(item => item.id === id)
  if (!current || !dirtyDrafts.has(id)) return
  const draft = current.draft[state.viewer] ?? "", internalDraft = current.internalDraft?.[state.viewer] ?? ""
  await preference(id, { draft, ...(isStaff(state.viewer) ? { internalDraft } : {}) })
  const latest = state.conversations.find(item => item.id === id)
  if (latest?.draft[state.viewer] === draft && (latest.internalDraft?.[state.viewer] ?? "") === internalDraft) dirtyDrafts.delete(id)
}
async function upload(id: string, file: File, stableId: string) {
  const requestEpoch = epoch
  const { values: policy } = await api<UploadPolicySettings>("/api/upload-policy")
  if (epoch !== requestEpoch) throw new DOMException("The signed-in account changed.", "AbortError")
  const channel = state.conversations.find(c => c.id === id)?.channel ?? "web"
  if (file.size > uploadInputLimit(policy, channel)) throw new Error("This attachment exceeds the current upload limit. Choose a smaller file.")
  const form = new FormData(); form.set("file", file)
  return request<{ id: string; name: string; content_type: string; size_bytes: number }>("/" + id + "/attachments", { method: "POST", headers: { "Idempotency-Key": stableId }, body: form, signal: AbortSignal.timeout(policy.uploadSeconds * 1000) })
}
async function create(channel: Channel, person: ActorId, subject: string, members?: ActorId[], assistant = false, aiProvider: "openai" | "chatgpt" = "openai") {
  const team = channel === "team"
  const data = { channel: channel === "info" ? "info_support" : channel === "portal" ? "customer" : channel, subject,
    ...(isStaff(state.viewer) ? team ? { kind: assistant ? "private_ai" : members ? "group" : "direct", ...(assistant ? { aiProvider } : {}), participantUserIds: assistant ? [] : (members ?? [person]).filter(id => id !== state.viewer).map(id => id.replace(/^staff:/, "")) } : { customerId: person.replace(/^customer:/, "") } : {}) }
  const result = await command<{ id: string }>("", data)
  await loadThread(result.id); emit({ selectedId: result.id, error: undefined }); return result.id
}
function ownPreferences(changes: OwnChanges) {
  ownChanges = { ...ownChanges, ...changes }
  const { presence, ...sounds } = changes
  emit({ ...sounds, ...(presence ? { presence: { ...state.presence, [state.viewer]: presence } } : {}) })
  clearTimeout(ownTimer)
  ownTimer = setTimeout(() => {
    const pending = ownChanges, requestEpoch = epoch; ownChanges = {}
    ownWrite = ownWrite.catch(() => {}).then(async () => {
      if (requestEpoch !== epoch) return
      const saved = await command<{ version: number }>("/preferences", { expectedVersion: state.preferencesVersion, ...pending }, "PATCH")
      emit({ preferencesVersion: saved.version })
    })
    background(ownWrite.catch(async error => { await bootstrap(); throw error }))
  }, 250)
}
export const communicationsStore = {
  subscribe(listener: () => void) {
    listeners.add(listener)
    if (listeners.size === 1) queueMicrotask(() => {
      if (!listeners.size) return
      const now = Date.now()
      if (state.notifications.some(item => item.snoozedUntil && item.snoozedUntil <= now && item.dismissed && !item.archived && !item.deleted)) emit({ notifications: state.notifications.map(item => ({ ...item, dismissed: Boolean(item.archived || item.deleted || (item.snoozedUntil ?? 0) > now) })) })
      scheduleNotificationWake()
    })
    const disconnect = connectCommunications()
    return () => { listeners.delete(listener); if (!listeners.size) clearTimeout(notificationWake); disconnect() }
  },
  getSnapshot: () => state,
  getServerSnapshot: () => initial,
}
export const communicationActions = {
  async controlAiRun(threadId: string, runId: string, action: "stop" | "retry") {
    const result = await command<{ id: string }>("/" + threadId + "/ai", { action, runId })
    try { await loadThread(threadId) } catch { emit({ error: "Your reply action was accepted. Refresh the conversation to see its latest status." }) }
    return result
  },
  publicSession(threadId?: string | null) {
    return request<PublicConversationSession>("/guest/session" + (threadId ? "?threadId=" + encodeURIComponent(threadId) : ""))
  },
  async continuePublic(threadId: string, automatic = false) {
    if (state.status !== "ready") await bootstrap()
    const data = await request<PublicConversationSession>("/guest/session", { method: "POST", body: JSON.stringify({ action: "resume", threadId, automatic }) })
    await loadThread(threadId); emit({ selectedId: threadId, error: undefined }); return data
  },
  clearPublicSelection() { if (surface() === "public") emit({ selectedId: "", error: undefined }) },
  async forgetPublic() {
    await request("/guest/session", { method: "POST", body: JSON.stringify({ action: "forget" }) })
    clearIdentity()
  },
  async claimPublic(threadId: string) {
    await request("/guest/session", { method: "POST", body: JSON.stringify({ action: "claim", threadId }) })
    await loadThread(threadId); emit({ selectedId: threadId, error: undefined }); return communicationActions.publicSession(threadId)
  },
  acceptPreferences(viewer: string, cacheScope: string, saved: Bootstrap["preferences"]) {
    if (state.viewer !== viewer || state.cacheScope !== cacheScope) return false
    emit({ presence: { ...state.presence, [state.viewer]: saved.presence }, sounds: saved.sounds, soundTone: saved.soundTone, soundVolume: saved.soundVolume, acceptingChats: saved.acceptingChats, maxActiveChats: saved.maxActiveChats, preferencesVersion: saved.version, error: undefined })
    return true
  },
  watchThread(id: string, active = true) {
    const capturedEpoch = epoch
    const previous = watchedThreads.get(id) ?? { retained: 0, active: 0 }
    watchedThreads.set(id, { retained: previous.retained + 1, active: previous.active + Number(active) })
    return () => { if (capturedEpoch !== epoch) return; const count = watchedThreads.get(id); if (!count || count.retained <= 1) watchedThreads.delete(id); else watchedThreads.set(id, { retained: count.retained - 1, active: count.active - Number(active) }) }
  },
  claimInline(owner:string,openChat?:()=>void){const lease=Symbol(owner);inlineOwners.set(lease,{owner,openChat});emit({inlineOwner:owner});return()=>{inlineOwners.delete(lease);emit({inlineOwner:currentInlineOwner()?.owner})}},
  openInlineChat(){const current=currentInlineOwner();if(!current?.openChat)return false;current.openChat();return true},
  reset() { inlineOwners.clear();clearIdentity() },
  async refresh() { await bootstrap(); if (state.status === "ready") await Promise.all([loadList(), loadNotifications()]) },
  refreshNotifications: loadNotifications,
  async readAll(target: "threads" | "notifications", filters: Record<string, string> = {}) {
    const generation = epoch
    const result = await command<{ updatedThreads: number; updatedNotifications: number }>("/read-all", { target, filters }, "POST", { "x-danaro-expected-actor": state.viewer })
    if (generation !== epoch) throw new DOMException("The signed-in account changed.", "AbortError")
    notificationReadGeneration++
    // Reconcile authoritative counts instead of clearing a client-side snapshot:
    // messages received after the server's watermark must remain unread.
    emit({ readRevision: (state.readRevision ?? 0) + 1 })
    const refreshed = await Promise.allSettled([loadList(), loadNotifications(), ...(state.selectedId ? [loadThread(state.selectedId)] : [])])
    if (generation !== epoch) throw new DOMException("The signed-in account changed.", "AbortError")
    if (refreshed.some(item => item.status === "rejected")) emit({ error: "Read state saved. Refresh this view to see the latest messages." })
    return result
  },
  async notificationPage(query: string) {
    const generation = epoch
    const data = await request<NotificationPage>("/notifications?" + query)
    if (generation !== epoch) throw new DOMException("The signed-in account changed.", "AbortError")
    profiles(data.profiles)
    return { ...data, items: notificationItems(data) }
  },
  async notificationBatch(ids: string[], action: "read_many" | "archive_many") {
    if (!ids.length) return
    const generation = epoch, eventIds = [...new Set(ids)].sort()
    await command("/notifications", { action, eventIds }, "PATCH")
    if (generation !== epoch) return
    notificationReadGeneration++
    const selected = new Set(eventIds)
    emit({ notifications: state.notifications.map(item => selected.has(item.id) ? { ...item, banner: false, ...(action === "read_many" ? { read: true } : { archived: true, dismissed: true }) } : item) })
    background(loadNotifications())
  },
  archiveNotification(id: string) { return changeNotification(id, { dismissed: true }) },
  changeNotificationRead(id: string, read: boolean) { return changeNotification(id, { read }) },
  deleteNotification(id: string) { return changeNotification(id, { deleted: true, dismissed: false }) },
  snoozeNotification(id: string, until: string) { return changeNotification(id, { snoozedUntil: until, dismissed: false }) },
  undoNotification(id: string) { return changeNotification(id, { dismissed: false, ...(state.notificationLifecycleAvailable ? { deleted: false, snoozedUntil: null } : {}) }) },
  clearError() { emit({ error: undefined }) },
  select(id: string) {
    if (state.selectedId === id && state.conversations.find(item => item.id === id)?.loaded) return
    emit({ selectedId: id }); background(loadThread(id))
  },
  loadEarlier(id: string) { return loadThread(id, true) },
  more() { if (state.nextCursor) background(loadList(true)) },
  search(value: string) { listSearch = value.trim(); background(loadList()) },
  async findThreads(query: string) {
    const page=await request<ThreadPage>("?"+query),known=new Map(state.conversations.map(item=>[item.id,item])),presence=profiles(page.profiles)
    const items=page.items.map(item=>thread(item,known.get(item.id)))
    for(const item of items)known.set(item.id,item)
    emit({conversations:[...known.values()].sort((a,b)=>b.updatedAt-a.updatedAt),presence})
    return {items,nextCursor:page.nextCursor,expoEvents:page.expoEvents}
  },
  async contacts(kind: "staff" | "customer", q = "", page = 1, phoneChannel?: "sms" | "whatsapp") {
    const data = await request<{ profiles: ActorProfile[]; more: boolean }>("/contacts?kind=" + kind + "&q=" + encodeURIComponent(q) + "&page=" + page + (phoneChannel ? "&channel=" + phoneChannel : ""))
    emit({ presence: profiles(data.profiles) }); return { ...data, ids: data.profiles.map(actorId) }
  },
  async startSupport(options: { fresh?: boolean } = {}) {
    if (state.status !== "ready") await bootstrap()
    if (surface() === "public" && state.status === "idle") { await request("/guest", { method: "POST" }); await bootstrap() }
    if (state.status !== "ready") throw new Error(state.error || "Sign in to start a conversation.")
    const channel = surface() === "customer" ? "portal" : "live"
    const existing = options.fresh ? undefined : state.conversations.find(item => !item.companyContext && !item.invoiceContext && item.mode !== "resolved" && item.channel === channel)
    if (existing) { await loadThread(existing.id); emit({ selectedId: existing.id }); return existing.id }
    if (!options.fresh && surface() !== "crm") { const id = await communicationActions.findSupport(); if (id) { emit({ selectedId: id }); return id } }
    return create(channel, state.viewer, "Conversation with Danaro")
  },
  async findSupport() {
    if (state.status !== "ready") await bootstrap()
    if (state.status !== "ready" || surface() === "crm") return null
    const { id } = await request<{ id: string | null }>("/support-current")
    if (id) await loadThread(id)
    return id
  },
  async companySupport(companyContext: import("@/modules/communications/company-context-contracts").CompanyConversationContext) {
    if (state.status !== "ready") await bootstrap()
    if (state.status !== "ready" || surface() !== "customer" || !state.viewer.startsWith("customer:")) throw new Error("Open company support from your verified My Danaro account.")
    const result = await command<{ id: string }>("", { channel: "customer", companyContext, subject: "" })
    await loadThread(result.id); emit({ selectedId: result.id, error: undefined }); return result.id
  },
  create,
  createGroup(members: ActorId[], subject: string) { return create("team", members[0] ?? state.viewer, subject, members) },
  async assistant(provider: "openai" | "chatgpt" = "openai") {
    const existing = state.conversations.find(item => item.assistant && (item.assistantProvider ?? "openai") === provider)
    const id = existing?.id ?? (await request<{ id: string | null }>("/assistant?provider=" + provider)).id
    if (id) { await loadThread(id); emit({ selectedId: id }); return id }
    return create("team", state.viewer, provider === "chatgpt" ? "Personal ChatGPT" : "Chat with DanAI", [], true, provider)
  },
  read(id: string, actor = state.viewer) {
    const current = state.conversations.find(item => item.id === id)
    if (actor !== state.viewer || !current || reading.has(id) || !(current.unread[actor] ?? 0) && !state.notifications.some(item => item.conversationId === id && !item.read && !item.dismissed)) return
    if (!current.loaded) { background(loadThread(id).then(() => communicationActions.read(id, actor))); return }
    reading.add(id)
    background(mutate(id, "read", current.cursor ? { through: current.cursor } : {}).finally(() => reading.delete(id)))
  },
  folder(id: string, folder: Conversation["folder"]) { background(preference(id, { folder, ...(folder === "snoozed" ? { snoozedUntil: new Date(Date.now() + 3600000).toISOString() } : {}) })) },
  snooze(id: string, minutes: number) { background(preference(id, { folder: "snoozed", snoozedUntil: new Date(Date.now() + minutes * 60000).toISOString() })) },
  wake(id: string) { background(preference(id, { folder: "inbox" })) },
  requestHuman(id: string, wait = false) { const request = mutate(id, "handoff"); if (wait) return request; background(request) },
  takeover(id: string, actor = state.viewer) { if (actor === state.viewer) background(mutate(id, "takeover")) },
  resumeAI(id: string) { background(mutate(id, "resume-ai")) },
  resolve(id: string) { background(mutate(id, "resolve")) },
  reopen(id: string) { background(mutate(id, isStaff(state.viewer) ? "takeover" : "handoff")) },
  assign(id: string, actor: ActorId) { background(mutate(id, "assign", { assignedUserId: actor.replace(/^staff:/, "") })) },
  retry(id: string, messageId: string) { background(mutate(id, "retry-email", { messageId })) },
  reloadThread(id: string) { return loadThread(id) },
  draft(id: string, actor: ActorId, text: string, internal = false) {
    if (actor !== state.viewer) return
    dirtyDrafts.add(id); updateThread(id, item => internal ? { ...item, internalDraft: { ...item.internalDraft, [actor]: text } } : { ...item, draft: { ...item.draft, [actor]: text } })
    clearTimeout(draftTimers.get(id)); draftTimers.set(id, setTimeout(() => background(persistDraft(id)), 800))
  },
  async send(id: string, actor: ActorId, text: string, attachments: Attachment[] = [], internal = false, voice?: Voice) {
    if (actor !== state.viewer) throw new Error("The signed-in account changed.")
    const sendEpoch = epoch
    const { values: policy } = await api<UploadPolicySettings>("/api/upload-policy")
    if (sendEpoch !== epoch || actor !== state.viewer) throw new DOMException("The signed-in account changed.", "AbortError")
    if (attachments.length + (voice ? 1 : 0) > policy.chatFiles) throw new Error(`Use up to ${policy.chatFiles} attachments.`)
    if (voice && voice.duration > policy.voiceMaxSeconds) throw new Error(`Voice messages may be up to ${policy.voiceMaxSeconds} seconds.`)
    await persistDraft(id)
    // Match the server's per-person parser capacity, preserving the attachment order.
    const uploaded = await mapWithConcurrency(attachments, policy.actorConcurrent, async file => {
      const blob = file.file ?? urls.get(file.url)
      if (!blob) return file.id
      const key = uploadKeys.get(blob) ?? crypto.randomUUID(); uploadKeys.set(blob, key)
      return (await upload(id, blob instanceof File ? blob : new File([blob], file.name, { type: blob.type }), key)).id
    })
    let recording: { attachmentId: string; duration: number; waveform: number[] } | undefined
    if (voice) {
      const blob = voice.blob ?? (voice.audioUrl ? urls.get(voice.audioUrl) : undefined)
      if (!blob) throw new Error("Record the voice message again before sending.")
      const signature = "voice:" + id + ":" + voice.id, key = retryKeys.get(signature) ?? crypto.randomUUID(); retryKeys.set(signature, key)
      const saved = await upload(id, new File([blob], "voice-message", { type: blob.type || voice.mimeType }), key)
      uploaded.push(saved.id); recording = { attachmentId: saved.id, duration: voice.duration, waveform: voice.waveform }
    }
    await command("/" + id, { action: "message", body: text, attachmentIds: uploaded, internal, ...(recording ? { voice: recording } : {}) })
    communicationActions.draft(id, actor, "", internal)
    // The accepted message must not become a failed send because a subsequent refresh fails.
    const refreshed = await Promise.allSettled([loadThread(id), persistDraft(id)])
    for (const attachment of attachments) if (urls.has(attachment.url)) { URL.revokeObjectURL(attachment.url); urls.delete(attachment.url) }
    if (voice?.audioUrl && urls.has(voice.audioUrl)) { URL.revokeObjectURL(voice.audioUrl); urls.delete(voice.audioUrl) }
    if (sendEpoch === epoch && actor === state.viewer) {
      const failure = refreshed.find(result => result.status === "rejected")
      if (failure?.status === "rejected") {
        report(failure.reason)
        if (sendEpoch === epoch) emit({ error: "Your message was accepted, but the latest conversation could not load. Retry the latest data; do not send the message again." })
      } else emit({ error: undefined })
    }
    return true
  },
  async sendTemplate(id: string, actor: ActorId, templateId: string, templateVersion: number, variables: Record<string, string>, body: string) {
    if (actor !== state.viewer || !isStaff(actor)) throw new Error("The signed-in staff account changed.")
    await command("/" + id, { action: "message", body, attachmentIds: [], internal: false, templateId, templateVersion, templateVariables: variables })
    await loadThread(id).catch(report)
    return true
  },
  reaction(id: string, messageId: string, actor: ActorId, emoji: string) {
    if (actor !== state.viewer) return
    const current = state.conversations.find(item => item.id === id)?.messages.find(item => item.id === messageId)
    background(command("/" + id + "/reactions", { messageId, emoji, active: !current?.reactions.some(item => item.emoji === emoji && item.by.includes(actor)) }).then(() => loadThread(id)))
  },
  notificationRead(id: string, read = true) { background(command("/notifications", { eventId: id, read, ...(read ? { bannerDismissed: true } : {}) }, "PATCH").then(loadNotifications)) },
  dismiss(id: string, permanent = false) { background(command("/notifications", { eventId: id, bannerDismissed: true, ...(permanent ? { dismissed: true } : {}) }, "PATCH").then(loadNotifications)) },
  restoreNotification(id: string) { background(command("/notifications", { eventId: id, dismissed: false, bannerDismissed: true }, "PATCH").then(loadNotifications)) },
  ownUrl(blob: Blob) { const url = URL.createObjectURL(blob); urls.set(url, blob); return url },
  presence(actor: ActorId, presence: Presence) { if (actor === state.viewer) ownPreferences({ presence }) },
  sounds(sounds: boolean) { communicationActions.soundSettings({ sounds }) },
  soundSettings(changes: Partial<SoundPreferences>) { ownPreferences(changes) },
}

